'use client';

import {
  HiArrowDownTray,
  HiCheckBadge,
  HiClock,
  HiDocumentText,
  HiPaperAirplane,
  HiShieldCheck,
} from 'react-icons/hi2';
import { Locale } from '../../../../i18n-config';
import SectionLabel from '../home/SectionLabel';
import { useTranslation } from '@/i18n/client';
import React from 'react';

type Props = {
  lang: Locale;
};

type FeatureKey = 'template' | 'requests' | 'audit' | 'methods' | 'expiry' | 'onboarding';

type Feature = {
  key: FeatureKey;
  Icon: typeof HiDocumentText;
  accent: string;
};

const FEATURES: Feature[] = [
  { key: 'template', Icon: HiDocumentText, accent: '#22D3EE' },
  { key: 'requests', Icon: HiPaperAirplane, accent: '#22D3EE' },
  { key: 'audit', Icon: HiShieldCheck, accent: '#A78BFA' },
  { key: 'methods', Icon: HiArrowDownTray, accent: '#22D3EE' },
  { key: 'expiry', Icon: HiClock, accent: '#22D3EE' },
  { key: 'onboarding', Icon: HiCheckBadge, accent: '#A78BFA' },
];

const ESignatureFeatures = ({ lang }: Props) => {
  const { t } = useTranslation(lang);

  return (
    <section
      id="esignature-features"
      className="px-6 py-24 lg:py-28"
      style={{
        background: '#0E1130',
        borderTop: '1px solid rgba(255,255,255,0.05)',
        color: '#F5F7FA',
      }}
    >
      <div className="mx-auto max-w-[1200px]">
        <SectionLabel num="01" label={t('ESignature/Features/badge')} />
        <h2
          className="font-bold tracking-tight"
          style={{
            fontSize: 'clamp(28px, 3.4vw, 48px)',
            lineHeight: 1.1,
            letterSpacing: '-0.02em',
            margin: '12px 0 14px',
            maxWidth: 820,
          }}
        >
          {t('ESignature/Features/title')}{' '}
          <span style={{ color: '#B4C1D8' }}>{t('ESignature/Features/headline_b')}</span>
        </h2>
        <p
          style={{
            color: '#B4C1D8',
            fontSize: 18,
            maxWidth: 720,
            lineHeight: 1.5,
            margin: '0 0 48px',
          }}
        >
          {t('ESignature/Features/subtitle')}
        </p>

        <div className="grid gap-5" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
          {FEATURES.map(({ key, Icon, accent }) => {
            return (
              <div
                key={key}
                className="rounded-2xl p-7 transition-all duration-200"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div
                  className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-5"
                  style={{
                    background: `${accent}1f`,
                    border: `1px solid ${accent}3a`,
                  }}
                >
                  <Icon className="w-6 h-6" style={{ color: accent }} />
                </div>
                <h3
                  className="font-semibold"
                  style={{
                    fontSize: 19,
                    letterSpacing: '-0.01em',
                    color: '#F5F7FA',
                    margin: '0 0 8px',
                  }}
                >
                  {t(`ESignature/Features/${key}_title`)}
                </h3>
                <p style={{ color: '#B4C1D8', fontSize: 14, lineHeight: 1.55, margin: 0 }}>
                  {t(`ESignature/Features/${key}_description`)}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ESignatureFeatures;
